import { Breadcrumb, Button, Card, Form, Input, Modal } from "antd";
import React, { useState, useCallback, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router";
import { loginDetailsSelector } from "../../store/selectors/app.selector";
import SettingsService from "../../services/settings.service";
import CommonOtp from "../../components/otp";
import {
  allowOnlyAlphaNumerics,
  allowOnlyAlphaNumericsWithSpecialChar,
  allowOnlyAlphabets,
} from "../../services/common.service";
import { ReactComponent as HomeIcon } from "../../assests/images/home-line.svg";
import { useTranslation } from "react-i18next";
import AuthService from "../../services/auth.service";
import { ReactComponent as TickIcon } from "../../assests/images/tickIcon.svg";
import OTP from "../../components/otp";

export default function ManageUserId() {
  const navigate = useNavigate();
  const location = useLocation();
  const [form] = Form.useForm();
  const [stage, setStage] = useState(1);
  const loginDetails = useSelector(loginDetailsSelector);
  const [loader, setLoader] = useState(false);
  const [message, setMessage] = useState("");
  const [formValues, setFormValues] = useState({});
  const [visible, setVisible] = useState(false);
  const [successVisible, setSuccessVisible] = useState(false);
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [otpEnabled, setOtpEnabled] = useState(false);
  const [formData, setFormdata] = useState([]);
  const [otp, setOtp] = useState("");

  useEffect(() => {
    if (Object.keys(loginDetails).length > 0) {
      form.setFieldsValue({
        currentUsername: loginDetails?.userName,
      });
    }
  }, []);

  const onVerify = useCallback(async (values) => {
    try {
      setLoader(true);
      setFormValues(values);
      const response = await SettingsService.usercredVerify({
        currentUsername: loginDetails?.userName,
        newUsername: values?.newUsername,
      });
      if (response.status === 200) {
        setLoader(false);
        if (response.data["status"] === "BAD_REQUEST") {
          setMessage(response.data["message"]);
          setVisible(true);
          return;
        }
        if (response.data["status"] === "OK") {
          setStage(2);
          return;
        }
      }
    } catch (error) {
      setLoader(false);
    }
  }, []);

  const onConfirm = useCallback(async (values) => {
    try {
      setLoader(true);
      const response = await SettingsService.changeUsernameConfirm(values);
      if (response.status === 200) {
        setLoader(false);
        setSuccessVisible(true);
        setMessage(response.data["message"]);
      }
    } catch (error) {
      setLoader(false);
    }
  }, []);

  function renderSwitch() {
    switch (stage) {
      case 1:
        return renderForm();

      case 2:
        return (
          <OTP
            otp={otp}
            setOtp={setOtp}
            onConfirm={(values) => onConfirm({ ...formData, ...values })}
            handleResendOtp={() => onConfirm()}
            cancelTransaction={() => {
              setStage(1);
              setFormdata({});
            }}
          />
        );

      default:
        return renderForm();
    }
  }

  function renderForm() {
    return (
      <>
        <p className="mb-3">
          {t("pages.settings.update_txt", {
            defaultValue:
              "Update your internet banking username of your own choice",
          })}
        </p>

        <div className="row">
          <div className="col-12 col-sm-12 col-md-12 col-lg-7">
            <Form layout="vertical" form={form} onFinish={onVerify}>
              <Form.Item label="Current Username" name="currentUsername">
                <Input
                  size="large"
                  readOnly
                  placeholder={t("pages.settings.user_id", {
                    defaultValue: "User Id",
                  })}
                />
              </Form.Item>

              <Form.Item
                label="New Username"
                name="newUsername"
                rules={[
                  { required: true, message: "New Username is required" },
                  {
                    min: 8,
                    max: 20,
                    message:
                      "New Username should be between 8 to 20 characters ",
                  },
                ]}
              >
                <Input
                  onKeyPress={(e) => allowOnlyAlphaNumericsWithSpecialChar(e)}
                  maxLength={20}
                  placeholder="New Username"
                  size="large"
                />
              </Form.Item>

              <Form.Item
                label="Confirm New Username"
                name="cNewUsername"
                rules={[
                  {
                    required: true,
                    message: "Confirm New Username is required",
                  },
                  ({ getFieldValue }) => ({
                    validator(rule, value) {
                      if (!value || getFieldValue("newUsername") === value) {
                        return Promise.resolve();
                      }
                      return Promise.reject(
                        "Confirm New Username do not match. Please try again."
                      );
                    },
                  }),
                ]}
              >
                <Input
                  onKeyPress={(e) => allowOnlyAlphaNumerics(e)}
                  maxLength={20}
                  placeholder="Confirm New Username"
                  size="large"
                />
              </Form.Item>
              <Button
                size="large"
                htmlType="submit"
                type="primary"
                block
                className="login-button"
              >
                {t("buttons.update_btn", {
                  defaultValue: "Update",
                })}
              </Button>
            </Form>
          </div>
          <div className="col-12 col-sm-12 col-md-5 col-lg-5">
            <Card className="application-card">
              <div>
                <h6>New Username Criteria Tips</h6>
                <ul>
                  <li>
                    Username length should not be less than 8 and not more than
                    20 characters.{" "}
                  </li>
                  <li>Username should not contain special characters</li>
                </ul>
              </div>
            </Card>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <div style={{ marginBottom: 20 }}>
        {location.pathname === "/manage-userId" ? (
         null
        ) : (
          <Breadcrumb separator=">">
            <Breadcrumb.Item>
              <span onClick={() => navigate("/dashboard")}>
                <HomeIcon height={14} />
              </span>
            </Breadcrumb.Item>
            <Breadcrumb.Item>
              <span onClick={() => navigate("/settings")}>Settings</span>
            </Breadcrumb.Item>
            <Breadcrumb.Item>
              <span>Manage Username</span>
            </Breadcrumb.Item>
          </Breadcrumb>
        )}
      </div>
      <Card size="small" className="mb-1 text-center breadcrumb-title-card">
        <div className="row">
          <div className="col-xs-12 col-sm-12 col-md-12 col-lg-12 col-xl-12 d-flex justify-content-left align-items-center heading mx-0">
            {/* <TitleIcon height={30} style={{ marginLeft: "-20px" }} /> */}
            <h3 className="title mb-0">Manage Username</h3>
          </div>
        </div>
      </Card>

      <div className="row">
        <div className="col-12 col-sm-12 col-md-12 col-lg-12">
          <Card className="application-card">
            <div>{renderSwitch()}</div>
          </Card>
        </div>
      </div>

      <Modal
        title={t("errorMesssages.error", { defaultValue: "Error" })}
        open={visible}
        footer={null}
        width={350}
        closable={false}
        maskClosable={false}
        mask={true}
      >
        <p style={{ marginBottom: 30, fontSize: 15 }}>{message}</p>
        <div align="right" className="mt-3">
          <Button
            type="link"
            onClick={() => {
              setVisible(false);
            }}
          >
            {t("buttons.okay_btn", {
              defaultValue: "Okay",
            })}
          </Button>
        </div>
      </Modal>

      <Modal
        open={successVisible}
        footer={null}
        width={480}
        closable={false}
        maskClosable={false}
        mask={true}
        className="custom-error-modal"
      >
        <div style={{ margin: "0 auto" }} align="center">
          <TickIcon height={65} />
          <h4 className="mb-3 mt-2">
            {t("common.success", {
              defaultValue: "Success",
            })}
          </h4>
          <p>{message}</p>

          <div align="center">
            <Button
              size="large"
              style={{ width: 100 }}
              onClick={() => {
                setSuccessVisible(false);
                navigate("/dashboard");
              }}
              type="primary"
            >
              {t("buttons.okay_btn", {
                defaultValue: "Okay",
              })}
            </Button>
          </div>
        </div>
      </Modal>
    </>
  );
}
